import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import type { CustomPlacement, GuideRenderContext } from '../types';
import { waitForElement } from '../wait-for-element';
import { BaseRenderer } from './base-renderer';
import { mountCustomFrame, toCssSize } from './custom-frame';

const DEFAULT_PLACEMENT: CustomPlacement = { mode: 'floating', position: 'bottom-right' };
const DEFAULT_OFFSET = 16;
const DEFAULT_WIDTH = 360;

/**
 * Renderiza una guía `custom` montando el HTML/CSS/JS del cliente en un
 * **iframe sandbox** (ver `custom-frame.ts`). Ubicación: `floating`
 * (esquina/centro del viewport) o `anchored` (Floating UI contra un selector
 * del host). La variante embebida en el flujo es `inline-custom`.
 */
export class CustomRenderer extends BaseRenderer {
  async render(context: GuideRenderContext): Promise<void> {
    const step = context.guide.guideSteps[0];
    if (!step || typeof step.html !== 'string' || step.html.length === 0) return;

    const placement: CustomPlacement = step.placement ?? DEFAULT_PLACEMENT;

    let anchor: Element | null = null;
    if (placement.mode === 'anchored') {
      const selector =
        placement.selector ?? step.selector ?? context.guide.activationRules.selector;
      if (typeof selector !== 'string' || selector.length === 0) return;
      anchor = await waitForElement(selector);
      if (!anchor) return; // sin ancla no mostramos (guía fantasma = peor UX)
    }

    const { root } = this.createHost();
    const doc = root.ownerDocument ?? document;

    const wrapper = doc.createElement('div');
    wrapper.className =
      placement.mode === 'anchored' ? 'veo-custom-anchored' : 'veo-custom-floating';

    const { iframe, cleanup } = mountCustomFrame(doc, step, context, {
      width: toCssSize(placement.width ?? DEFAULT_WIDTH),
      fixedHeight: placement.height ?? null,
    });
    this.registerCleanup(cleanup);

    wrapper.appendChild(iframe);
    // Sin ✕ del host: las guías custom controlan su propio cierre desde su HTML
    // (`window.veo.dismiss()`). Un botón inyectado no encaja con su diseño.
    root.appendChild(wrapper);

    if (placement.mode === 'floating') {
      applyFloatingPosition(wrapper, placement);
    } else if (anchor) {
      this.registerCleanup(await this.setupAnchoredPosition(anchor, wrapper, placement));
    }

    context.onInteraction({ guideId: context.guide.guideId, stepIndex: 0, action: 'shown' });
  }

  private async setupAnchoredPosition(
    anchor: Element,
    wrapper: HTMLElement,
    placement: CustomPlacement,
  ): Promise<() => void> {
    const side = placement.side ?? 'bottom';
    const update = async (): Promise<void> => {
      const { x, y } = await computePosition(anchor, wrapper, {
        placement: side,
        strategy: 'fixed',
        middleware: [offset(placement.offsetY ?? 8), flip(), shift({ padding: 8 })],
      });
      wrapper.style.left = `${x}px`;
      wrapper.style.top = `${y}px`;
    };
    await update();

    const reposition = (): void => {
      void update();
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }
}

/** Fija el wrapper en una esquina/centro del viewport según `position`. */
function applyFloatingPosition(wrapper: HTMLElement, placement: CustomPlacement): void {
  const position = placement.position ?? 'bottom-right';
  const ox = `${placement.offsetX ?? DEFAULT_OFFSET}px`;
  const oy = `${placement.offsetY ?? DEFAULT_OFFSET}px`;
  const s = wrapper.style;

  if (position === 'center') {
    s.top = '50%';
    s.left = '50%';
    s.transform = 'translate(-50%, -50%)';
    return;
  }

  const [vertical, horizontal] = position.split('-');
  if (vertical === 'top') s.top = oy;
  else s.bottom = oy;

  if (horizontal === 'left') {
    s.left = ox;
  } else if (horizontal === 'right') {
    s.right = ox;
  } else {
    s.left = '50%';
    s.transform = 'translateX(-50%)';
  }
}
